// FxTicker.js — FX rate display with CBK rate, live mid, locked forward, and 7-day sparkline.
// Uses inline SVG for the sparkline — no chart library required.

import React, { useMemo } from 'react';
import { ArrowRightLeft } from 'lucide-react';

function fmtRate(value, decimals = 4) {
  if (value == null) return '--';
  return parseFloat(value).toFixed(decimals);
}

function fmtPctMove(entry, current) {
  if (entry == null || current == null) return '--';
  const move = ((current - entry) / entry) * 100;
  const sign = move >= 0 ? '+' : '';
  return `${sign}${move.toFixed(2)}%`;
}

function pctMoveColor(entry, current) {
  if (entry == null || current == null) return 'text-zinc-500';
  const move = current - entry;
  if (Math.abs(move) < 0.0001) return 'text-zinc-500';
  return move > 0 ? 'text-red-600' : 'text-emerald-600';
}

/**
 * Build a simple SVG path from an array of numeric values.
 * Returns a polyline path string scaled to fit within the given width/height.
 */
function buildSparklinePath(values, width = 120, height = 32) {
  if (!values || values.length < 2) return '';

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);

  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return `M${points.join(' L')}`;
}

export default function FxTicker({ entryFxRate, lockedForwardRate, latestMark, marks = [] }) {
  const liveMid = latestMark?.spot_kes_chf ?? null;

  // Extract last 7 data points for the sparkline
  const sparklineValues = useMemo(() => {
    if (!marks || marks.length === 0) return [];
    const recent = marks.slice(-7);
    return recent.map(m => parseFloat(m.spot_kes_chf || m.spot || 0)).filter(v => !isNaN(v) && v > 0);
  }, [marks]);

  const sparklinePath = useMemo(() => buildSparklinePath(sparklineValues), [sparklineValues]);

  // Determine sparkline color based on trend
  const sparklineColor = useMemo(() => {
    if (sparklineValues.length < 2) return '#71717a';
    const first = sparklineValues[0];
    const last = sparklineValues[sparklineValues.length - 1];
    if (last > first) return '#dc2626'; // KES weakening (bad for investor)
    if (last < first) return '#059669'; // KES strengthening
    return '#71717a';
  }, [sparklineValues]);

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            FX Rates (KES/CHF)
          </span>
        </div>
        {liveMid != null && (
          <span className={`font-mono text-[10px] font-semibold ${pctMoveColor(entryFxRate, liveMid)}`}>
            {fmtPctMove(entryFxRate, liveMid)} from entry
          </span>
        )}
      </div>

      {/* Rate tiles */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* CBK Daily Rate */}
        <div className="border border-zinc-200 p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            CBK Daily Rate
          </p>
          <p className="font-mono text-lg font-semibold tabular-nums text-zinc-900">
            {fmtRate(entryFxRate, 2)}
          </p>
        </div>

        {/* Live Mid Rate */}
        <div className="border border-zinc-200 p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Live Mid Rate
          </p>
          <p className="font-mono text-lg font-semibold tabular-nums text-zinc-900">
            {fmtRate(liveMid, 4)}
          </p>
          {liveMid != null && (
            <p className="font-mono text-[9px] text-zinc-400 mt-0.5">
              Updated {latestMark?.mark_date || 'recently'}
            </p>
          )}
        </div>

        {/* Locked Forward Rate */}
        <div className="border border-zinc-200 p-3" style={{ borderColor: '#4D4D4D' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Locked Forward
          </p>
          <p className="font-mono text-lg font-semibold tabular-nums text-zinc-900">
            {fmtRate(lockedForwardRate, 4)}
          </p>
          {lockedForwardRate != null && entryFxRate != null && (
            <p className="font-mono text-[9px] text-zinc-400 mt-0.5">
              Prem: {(((lockedForwardRate - entryFxRate) / entryFxRate) * 100).toFixed(2)}%
            </p>
          )}
        </div>
      </div>

      {/* 7-Day Sparkline */}
      {sparklineValues.length >= 2 && (
        <div className="border-t border-zinc-200 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">
            7-Day Spot Trend
          </p>
          <div className="flex items-center gap-3">
            <svg
              width="120"
              height="32"
              viewBox="0 0 120 32"
              className="flex-shrink-0"
              aria-label="7-day FX spot sparkline"
            >
              <path
                d={sparklinePath}
                fill="none"
                stroke={sparklineColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[9px] text-zinc-400">
                L: {fmtRate(Math.min(...sparklineValues), 2)}
              </span>
              <span className="font-mono text-[9px] text-zinc-400">
                H: {fmtRate(Math.max(...sparklineValues), 2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {sparklineValues.length < 2 && (
        <div className="border-t border-zinc-200 pt-3">
          <p className="font-mono text-[10px] text-zinc-400">
            Sparkline available after 2+ daily marks.
          </p>
        </div>
      )}
    </div>
  );
}
